import { useEffect, useState } from 'react';

import { healthPlanProviders } from '../pages/healthPlanProviders';
import { listHealthPlanImports } from '../services/healthPlanImportsService';
import type { HealthPlanProvider } from '../services/healthPlanTypes';
import { listFoodBenefitImports } from '../services/foodBenefitImportsService';
import { listFuneralAssistanceImports } from '../services/funeralAssistanceImportsService';
import { listPetPlanImports } from '../services/petPlanImportsService';
import { listHealthPromotionImports } from '../services/healthPromotionImportsService';
import { listPayrollImports } from '../services/payrollImportsService';
import type { PetPlanImportDocument } from '../services/petPlanTypes';

export type BenefitCompetenceTotalsRow = {
  competence: string;
  sortKey: string | null;
  healthPlan: number;
  foodBenefit: number;
  funeralAssistance: number;
  healthPromotion: number;
  petPlan: number;
  total: number;
};

export function useBenefitCompetenceTotals() {
  const [totals, setTotals] = useState<BenefitCompetenceTotalsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchTotals = async () => {
      setLoading(true);
      setError(null);
      const rowsMap = new Map<string, BenefitCompetenceTotalsRow>();
      let partialFailure = false;
      let deduction8111ByCompetence = new Map<string, number>();
      let deductionFoodByCompetence = new Map<string, number>();
      let deductionFuneralByCompetence = new Map<string, number>();

      const normalizeCompetenceLabel = (value?: string | null): string => {
        if (!value) return '';
        const trimmed = value.trim();
        if (!trimmed) return '';
        if (/^\d{6}$/.test(trimmed)) {
          return `${trimmed.slice(0, 4)}-${trimmed.slice(4)}`;
        }
        return trimmed;
      };

      const getRow = (competence?: string | null): BenefitCompetenceTotalsRow & { healthPlanGross?: number } => {
        const normalized = normalizeCompetenceLabel(competence);
        const label = normalized || 'Sem competência';
        const cached = rowsMap.get(label);
        if (cached) return cached as BenefitCompetenceTotalsRow & { healthPlanGross?: number };
        const row: BenefitCompetenceTotalsRow & { healthPlanGross?: number } = {
          competence: label,
          sortKey: normalized || null,
          healthPlan: 0,
          foodBenefit: 0,
          funeralAssistance: 0,
          healthPromotion: 0,
          petPlan: 0,
          total: 0,
          healthPlanGross: 0,
        };
        rowsMap.set(label, row);
        return row;
      };

      const fetchHealthPlanTotals = async () => {
        const responses = await Promise.allSettled(
          healthPlanProviders.map((provider) => listHealthPlanImports(provider.slug as HealthPlanProvider)),
        );
        responses.forEach((result) => {
          if (result.status === 'fulfilled') {
            result.value.forEach((doc) => {
              const row = getRow(doc.billingPeriod);
              const premium = doc.totals?.premiumHealth ?? 0;
              row.healthPlanGross = (row.healthPlanGross ?? 0) + premium;
            });
          } else {
            partialFailure = true;
            console.error('[useBenefitCompetenceTotals] failed to load health plan imports', result.reason);
          }
        });
      };

      const fetchPayrollDeductions = async () => {
        try {
          const payrollImports = await listPayrollImports();
          const mapHealth = new Map<string, number>();
          const mapFood = new Map<string, number>();
          const mapFuneral = new Map<string, number>();
          payrollImports.forEach((importDoc) => {
            const competence = normalizeCompetenceLabel(importDoc.competence);
            const byCode =
              importDoc.totals?.deduction?.byCode ?? importDoc.totals?.deductions?.byCode ?? importDoc.totals?.deductions ?? {};
            const raw8111 = byCode['8111']?.total ?? byCode['8111'];
            const raw202 = byCode['202']?.total ?? byCode['202'];
            const raw233 = byCode['233']?.total ?? byCode['233'];
            const raw243 = byCode['243']?.total ?? byCode['243'];
            const val8111 = Number.isFinite(Number(raw8111)) ? Number(raw8111) : 0;
            const val202 = Number.isFinite(Number(raw202)) ? Number(raw202) : 0;
            const val233 = Number.isFinite(Number(raw233)) ? Number(raw233) : 0;
            const val243 = Number.isFinite(Number(raw243)) ? Number(raw243) : 0;
            mapHealth.set(competence || 'Sem competência', (mapHealth.get(competence || 'Sem competência') ?? 0) + val8111);
            mapFood.set(competence || 'Sem competência', (mapFood.get(competence || 'Sem competência') ?? 0) + val202 + val233);
            mapFuneral.set(competence || 'Sem competência', (mapFuneral.get(competence || 'Sem competência') ?? 0) + val243);
          });
          deduction8111ByCompetence = mapHealth;
          deductionFoodByCompetence = mapFood;
          deductionFuneralByCompetence = mapFuneral;
        } catch (error) {
          partialFailure = true;
          console.error('[useBenefitCompetenceTotals] failed to load payroll imports for 8111', error);
        }
      };

      const getPetPremiumTotal = (item: PetPlanImportDocument): number => {
        const fromRecords = Array.isArray(item.recordsPreview)
          ? item.recordsPreview.reduce((sum: number, record: any) => {
              const premium = typeof record?.premium === 'number' ? record.premium : typeof record?.amount === 'number' ? record.amount : 0;
              return sum + premium;
            }, 0)
          : 0;
        const totalsAmount = typeof item.totals?.amount === 'number' ? item.totals.amount : 0;
        return fromRecords > 0 ? fromRecords : totalsAmount;
      };

      const fetchHealthPromotionTotals = async () => {
        try {
          const imports = await listHealthPromotionImports();
          imports.forEach((item) => {
            const row = getRow(item.billingPeriod);
            const value = item.totals?.amount ?? 0;
            row.healthPromotion += value;
          });
        } catch (error) {
          partialFailure = true;
          console.error('[useBenefitCompetenceTotals] failed to load health promotion imports', error);
        }
      };

      const fetchFoodBenefitTotals = async () => {
        try {
          const imports = await listFoodBenefitImports();
          imports.forEach((item) => {
            const row = getRow(item.billingPeriod);
            const value = item.totals?.amount ?? 0;
            row.foodBenefit += value;
          });
        } catch (error) {
          partialFailure = true;
          console.error('[useBenefitCompetenceTotals] failed to load food benefit imports', error);
        }
      };

      const fetchFuneralTotals = async () => {
        try {
          const imports = await listFuneralAssistanceImports();
          imports.forEach((item) => {
            const row = getRow(item.billingPeriod);
            const value = item.totals?.amount ?? 0;
            row.funeralAssistance += value;
          });
        } catch (error) {
          partialFailure = true;
          console.error('[useBenefitCompetenceTotals] failed to load funeral assistance imports', error);
        }
      };

      const fetchPetTotals = async () => {
        try {
          const imports = await listPetPlanImports();
          imports.forEach((item) => {
            const row = getRow(item.billingPeriod);
            const value = getPetPremiumTotal(item);
            row.petPlan += value;
          });
        } catch (error) {
          partialFailure = true;
          console.error('[useBenefitCompetenceTotals] failed to load pet plan imports', error);
        }
      };

      try {
        await fetchPayrollDeductions();
        await Promise.all([
          fetchHealthPlanTotals(),
          fetchHealthPromotionTotals(),
          fetchFoodBenefitTotals(),
          fetchFuneralTotals(),
          fetchPetTotals(),
        ]);
        if (!active) return;
        const sortedRows = Array.from(rowsMap.values())
          .map((row) => {
            const key = row.sortKey || '';
            const deductionHealth = deduction8111ByCompetence.get(key) ?? 0;
            const deductionFood = deductionFoodByCompetence.get(key) ?? 0;
            const deductionFuneral = deductionFuneralByCompetence.get(key) ?? 0;
            const netHealth = Math.max((row as any).healthPlanGross - deductionHealth, 0);
            const netFood = Math.max(row.foodBenefit - deductionFood, 0);
            const netFuneral = Math.max(row.funeralAssistance - deductionFuneral, 0);
            row.healthPlan = netHealth;
            row.foodBenefit = netFood;
            row.funeralAssistance = netFuneral;
            row.total = netHealth + netFood + row.healthPromotion + netFuneral + row.petPlan;
            delete (row as any).healthPlanGross;
            return row;
          })
          .sort((a, b) => {
            if (a.sortKey === b.sortKey) return 0;
            if (!a.sortKey) return 1;
            if (!b.sortKey) return -1;
            return b.sortKey.localeCompare(a.sortKey);
          });
        setTotals(sortedRows);
        setError(partialFailure ? 'Nem todos os benefícios foram carregados.' : null);
      } catch (error) {
        console.error('[useBenefitCompetenceTotals] failed to aggregate totals', error);
        if (active) {
          setTotals([]);
          setError('Não foi possível carregar os benefícios.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void fetchTotals();
    return () => {
      active = false;
    };
  }, []);

  return { totals, loading, error };
}
